import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Building2,
  Copy,
  Check,
  Smartphone,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentPage = () => {
  const { reference } = useParams();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    fetchPaymentData();
  }, [reference]);

  const fetchPaymentData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pay/${reference}`);
      if (res.ok) {
        const data = await res.json();
        setPaymentData(data);
      } else {
        const err = await res.json();
        setError(err.detail || 'Payment not found');
      }
    } catch (error) {
      setError('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(null), 2000);
  };

  const getNetworkColor = (network) => {
    return network === 'MTN' 
      ? 'bg-yellow-100 text-yellow-700 border-yellow-200' 
      : 'bg-orange-100 text-orange-700 border-orange-200';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin">
          <Smartphone className="w-8 h-8 text-teal-500" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Payment Not Found</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  // Handle different payment statuses
  if (paymentData.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-gray-500" />
          </div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Payment Expired</h1>
          <p className="text-slate-500">This payment request has expired. Please ask the merchant for a new payment link.</p>
        </div>
      </div>
    );
  }

  if (paymentData.status === 'confirmed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Payment Completed</h1>
          <p className="text-slate-500">This payment has already been received. Thank you!</p>
        </div>
      </div>
    );
  }

  if (paymentData.status === 'cancelled') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Payment Cancelled</h1>
          <p className="text-slate-500">This payment request was cancelled by the merchant.</p>
        </div>
      </div>
    );
  }

  // Pending payment - show payment instructions
  const { payment, merchant, payment_sources } = paymentData;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Building2 className="w-6 h-6" />
            </div>
            <div>
              <p className="text-teal-100 text-sm">Payment to</p>
              <h1 className="text-xl font-semibold">{merchant.business_name}</h1>
            </div>
          </div>
          
          {payment.amount ? (
            <div className="text-center py-4">
              <p className="text-teal-100 text-sm mb-1">Amount to Pay</p>
              <p className="text-4xl font-bold">
                {payment.currency} {payment.amount.toLocaleString()}
              </p>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-teal-100 text-sm">Amount</p>
              <p className="text-xl font-medium">Any amount</p>
            </div>
          )}
          
          {payment.description && (
            <p className="text-center text-teal-100 text-sm mt-2">{payment.description}</p>
          )}
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Reference */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-600">Reference Number</span>
              <button
                onClick={() => copyToClipboard(payment.reference, 'ref')}
                className="flex items-center gap-1 text-teal-500 hover:text-teal-600 text-sm"
              >
                {copied === 'ref' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied === 'ref' ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="bg-slate-100 rounded-lg p-4 text-center">
              <p className="text-2xl font-mono font-bold text-slate-800 tracking-wider">
                {payment.reference}
              </p>
            </div>
            <div className="flex items-center gap-2 mt-2 text-amber-600 bg-amber-50 rounded-lg p-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <p className="text-xs">Include this reference in your payment message</p>
            </div>
          </div>

          {/* Payment Methods */}
          <div>
            <h2 className="text-sm font-medium text-slate-600 mb-3">Send Mobile Money to:</h2>
            
            {payment_sources && payment_sources.length > 0 ? (
              <div className="space-y-3">
                {payment_sources.map((source, index) => (
                  <div
                    key={index}
                    className={`border rounded-xl p-4 ${getNetworkColor(source.network)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          source.network === 'MTN' ? 'bg-yellow-200' : 'bg-orange-200'
                        }`}>
                          <Phone className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">{source.network}</p>
                          <p className="font-mono text-lg">{source.phone_number}</p>
                          {source.label && (
                            <p className="text-xs opacity-70">{source.label}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => copyToClipboard(source.phone_number, `phone-${index}`)}
                        className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                      >
                        {copied === `phone-${index}` ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500">
                <p>No payment methods available</p>
                <p className="text-sm mt-1">Please contact the merchant directly</p>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="mt-6 bg-blue-50 rounded-xl p-4">
            <h3 className="font-medium text-blue-800 mb-2">How to Pay:</h3>
            <ol className="text-sm text-blue-700 space-y-2">
              <li className="flex gap-2">
                <span className="font-semibold">1.</span>
                <span>Open your mobile money app</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold">2.</span>
                <span>Send money to one of the numbers above</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold">3.</span>
                <span>Include <strong>{payment.reference}</strong> in the message</span>
              </li>
              <li className="flex gap-2">
                <span className="font-semibold">4.</span>
                <span>Your payment will be confirmed automatically</span>
              </li>
            </ol>
          </div>

          {/* Expiry notice */}
          {payment.expires_at && (
            <div className="mt-4 flex items-center justify-center gap-2 text-slate-500 text-sm">
              <Clock className="w-4 h-4" />
              <span>
                Expires: {new Date(payment.expires_at).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
          <p className="text-center text-xs text-slate-400">
            Powered by <span className="font-semibold text-teal-600">FlowLedger</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
